import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useCancelDeal, type Deal } from './useDeals';

interface Props {
  open: boolean;
  onClose: () => void;
  deal: Deal | undefined;
}

/**
 * Cancelling releases the unit and voids the commission, so the reason is
 * mandatory — the database refuses a blank one. Without it, "why did we lose that
 * unit?" is unanswerable six months later.
 */
export function CancelDealDialog({ open, onClose, deal }: Props) {
  const { t } = useTranslation();
  const cancelDeal = useCancelDeal();

  const [reason, setReason] = useState('');
  const [refund, setRefund] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setRefund(false);
    setError(null);
  }, [open]);

  const submit = async () => {
    if (!deal) return;
    if (!reason.trim()) {
      setError(t('deals.reasonRequired'));
      return;
    }
    try {
      await cancelDeal.mutateAsync({ dealId: deal.id, reason: reason.trim(), refund });
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('common.error'));
    }
  };

  return (
    <Modal open={open} title={t('deals.cancelDeal')} onClose={onClose}>
      <div className="flex flex-col gap-3">
        {error ? <Alert tone="error">{error}</Alert> : null}

        <Alert tone="info">{t('deals.cancelWarning')}</Alert>

        <Input
          label={t('deals.cancelReason')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="size-4 rounded border-slate-300"
            checked={refund}
            onChange={(e) => setRefund(e.target.checked)}
          />
          {t('deals.markRefunded')}
        </label>

        <div className="mt-2 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" loading={cancelDeal.isPending} onClick={() => void submit()}>
            {t('deals.cancelDeal')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
